/**
 * Pressure Role - Format Pillar
 *
 * Formatação de valores de pressão para exibição.
 *
 * @example
 * import { formatPressure, getPressureSymbol, getPressureName } from '@attrx/role-morphic/pressure/format';
 *
 * formatPressure('bar', 2.5);                     // "2.5 bar"
 * formatPressure('bar', 2.5, { verbose: true });  // "2.5 bar"
 * getPressureSymbol('pascal');                    // "Pa"
 * getPressureName('atmosphere', 2);               // "atmospheres"
 */

import { PRESSURE_UNITS, type PressureUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface PressureFormatOptions {
  /** Número de casas decimais */
  decimals?: number;
  /** Usar nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação numérica */
  locale?: string;
  /** Notação: standard, scientific, compact */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de pressão para exibição
 *
 * @param variant - Unidade
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatPressure('bar', 2.5);                     // "2.5 bar"
 * formatPressure('bar', 2.5, { verbose: true });  // "2.5 bar"
 * formatPressure('atmosphere', 1, { verbose: true }); // "1 atmosphere"
 * formatPressure('pascal', 1000, { decimals: 0 }); // "1000 Pa"
 */
export function formatPressure(
  variant: PressureUnit,
  value: number,
  options: PressureFormatOptions = {},
): string {
  const config = PRESSURE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown pressure unit: ${variant}`);
  }

  const { decimals, verbose = false, locale, notation = "standard" } = options;

  // Format number
  let formattedNumber: string;

  if (locale || notation !== "standard") {
    const intlOptions: Intl.NumberFormatOptions = { notation };
    if (decimals !== undefined) {
      intlOptions.minimumFractionDigits = decimals;
      intlOptions.maximumFractionDigits = decimals;
    }
    formattedNumber = new Intl.NumberFormat(locale, intlOptions).format(value);
  } else if (decimals !== undefined) {
    formattedNumber = value.toFixed(decimals);
  } else {
    // Smart formatting: remove trailing zeros
    formattedNumber = Number(value.toPrecision(12)).toString();
  }

  // Get unit label
  const unitLabel = verbose
    ? value === 1
      ? config.singular
      : config.plural
    : config.symbol;

  return `${formattedNumber} ${unitLabel}`;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Retorna o símbolo de uma unidade
 *
 * @example
 * getPressureSymbol('bar');        // "bar"
 * getPressureSymbol('atmosphere'); // "atm"
 */
export function getPressureSymbol(variant: PressureUnit): string {
  const config = PRESSURE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown pressure unit: ${variant}`);
  }
  return config.symbol;
}

/**
 * Retorna o nome de uma unidade (singular ou plural)
 *
 * @param variant - Unidade
 * @param count - Quantidade para determinar singular/plural (default: 1)
 *
 * @example
 * getPressureName('atmosphere', 1);  // "atmosphere"
 * getPressureName('atmosphere', 2);  // "atmospheres"
 */
export function getPressureName(variant: PressureUnit, count: number = 1): string {
  const config = PRESSURE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown pressure unit: ${variant}`);
  }
  return count === 1 ? (config.singular || config.symbol) : (config.plural || config.symbol);
}
